import { Box, Divider, Typography } from '@mui/material';
import { unwrapResult } from '@reduxjs/toolkit';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { UseFormSetError } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Oval } from 'react-loader-spinner';
import { useSelector } from 'react-redux';

import { useAppDispatch } from 'store/store';
import { editUserStories, saveUserStories } from 'store/ticket-service/asyncActions';
import {
	getActiveStep,
	getActiveSubStage,
	getActiveTicket,
	getEditMode,
	getIsSummaryLoading,
	getIsTicketNavigationButtonsAvailable,
	getVoiceMode,
} from 'store/ticket-service/selector';
import {
	setActiveStep,
	setActiveSubStage,
	setDetailedViewTicketSummary,
	setEditMode,
	setIsStreaming,
	setIsSummaryLoading,
	setIsTicketUpdate,
	setIsTyping,
	updateLocalTicket,
} from 'store/ticket-service/ticketSlice';
import { ISummaryStory, ISummaryStoryWithId, TicketType } from 'store/ticket-service/types';
import { fetchUserBalance } from 'store/user-service/asyncActions';
import { getChoosedFile } from 'store/user-service/selector';
import { triggerTicketSave } from 'store/user-service/userSlice';

import { useEventBus } from 'utils/EventBus';
import { getNumericEnumLength } from 'utils/getNumericEnumLength';

import {
	getConversation,
	getDescription,
	getPotentialUserStoriesStream,
	getUserStoriesStream,
	getUserStoryAcceptanceCriteria,
	getUserStoryScenario,
	handleNotEnoughTokensError,
} from 'core/chains/ChatGptRequests';
import { notEnoughTokensErrorMessage } from 'core/constants/chatGptRequestRegenerateMessages';
import {
	ChatGPTSubStageTemplateNames,
	GeneralStepConversationTopics,
	MessageActionStates,
	MessageActionTypes,
	MessageSender,
	NamespacesList,
	Statuses,
	TicketStages,
} from 'core/enums/common';
import { getIsActiveTicketHasEmptyScenariosOrCriteria } from 'core/helpers/get-active-ticket-scenarios-criterias-length';
import { errorNotify } from 'core/helpers/toasts';
import useSpeechToText from 'core/hooks/useSpeechToText';
import useTextToSpeech from 'core/hooks/useTextToSpeech';

import { FormTitleValue } from 'pages/Home';

import { IMessage } from 'components/Chatbot/Message/types';
import { UserStoriesStreaming } from 'components/Streaming/UserStoriesStreaming';
import { EditGeneralStep } from 'components/TicketSteps/GeneralStep/EditGeneralStep';
import { CustomActionTooltip } from 'components/shared/CustomActionTooltip';
import { TitleText } from 'components/shared/CustomText/TitleText';

import { ReactComponent as ArrowLeft } from 'assets/icons/arrow-left.svg';
import { ReactComponent as Stars } from 'assets/icons/stars.svg';
import { regularTextColor, titleTextColor } from 'assets/styles/_palette';

import GeneralStepSummary from './UserStoriesList';
import * as Styled from './styled';

interface GeneralStepProps {
	messages: IMessage[];
	setMessages: (messages: Array<IMessage>) => void;
	ticketTitle: string;
	setTitleError: UseFormSetError<FormTitleValue>;
	isChatLoading: boolean;
	setIsChatLoading: (value: boolean) => void;
}

const questionsBetweenGenerateRequests = Object.keys(GeneralStepConversationTopics).length * 2;

export const GeneralStep: FC<GeneralStepProps> = ({
	messages,
	setMessages,
	ticketTitle,
	setTitleError,
	isChatLoading,
	setIsChatLoading,
}) => {
	const { t: chatMessagesTranslate } = useTranslation(NamespacesList['chatMessages']);
	const { t: generalStepTranslate, i18n } = useTranslation(NamespacesList['generalStep']);
	const { t: commonTranslate } = useTranslation(NamespacesList['common']);

	const defaultWelcomeMessageRequest = (discussionCategory: string) =>
		`${commonTranslate('modelsInput.defaultMessagePart1')} ${commonTranslate(
			`modelsInput.${discussionCategory}`
		)} ${commonTranslate('modelsInput.defaultMessagePart2')}. ${commonTranslate(
			'modelsInput.defaultMessagePart3'
		)}`;

	const dispatch = useAppDispatch();
	const { addEventListener, removeEventListener, dispatchEvent } = useEventBus();
	const { playText, hasFinishedSpeaking } = useTextToSpeech();
	const { startRecognition } = useSpeechToText();

	const activeStep = useSelector(getActiveStep);
	const activeSubStage = useSelector(getActiveSubStage);
	const activeTicket = useSelector(getActiveTicket);
	const isSummaryLoading = useSelector(getIsSummaryLoading);
	const voiceMode = useSelector(getVoiceMode);
	const editMode = useSelector(getEditMode);
	const choosedFile = useSelector(getChoosedFile);
	const isNavigationButtonAvailable = useSelector(getIsTicketNavigationButtonsAvailable);

	const userStoriesStreamName = 'userStoryCreated';

	const activeTicketRef = useRef<TicketType | null>(null);

	useEffect(() => {
		activeTicketRef.current = activeTicket;
	}, [activeTicket]);

	const [streamMessage, setStreamMessage] = useState<Array<string>>([]);
	const [isStreamMessageLoading, setIsStreamMessageLoading] = useState<boolean>(false);
	const [isDescriptionLoading, setIsDescriptionLoading] = useState<boolean>(false);

	const summaryStories = useMemo(
		() => activeTicket?.ticketSummaries || [],
		[activeTicket?.ticketSummaries, activeTicket?.id]
	);

	useEffect(() => {
		dispatch(setEditMode(false));
	}, [activeTicket?.id]);

	const ticketContext = useMemo(() => {
		return activeTicket?.context;
	}, [activeTicket?.context]);

	const hasAcceptedGenerateRequest: boolean = useMemo(() => {
		const acceptedGenerateRequest = messages.find((message) => {
			return (
				message.subStage === activeSubStage &&
				message?.action?.type === MessageActionTypes.generate &&
				message?.action?.state === MessageActionStates.accept
			);
		});

		return Boolean(acceptedGenerateRequest);
	}, [messages, activeSubStage]);

	const areScenariosGenerated = useMemo(
		() =>
			getIsActiveTicketHasEmptyScenariosOrCriteria(
				activeTicket?.ticketSummaries,
				'summaryScenarios',
				activeSubStage,
			),
		[activeTicket?.ticketSummaries, activeSubStage]
	);

	const areAcceptanceCriteriaGenerated = useMemo(
		() =>
			getIsActiveTicketHasEmptyScenariosOrCriteria(
				activeTicket?.ticketSummaries,
				'summaryAcceptanceCriteria',
				activeSubStage,
			),
		[activeTicket?.ticketSummaries, activeSubStage]
	);

	useEffect(() => {
		addEventListener('messageSend', handleMessageSend);
		addEventListener('generateAccept', handlePotentialSummary);
		addEventListener('generateDecline', declineSummaryGeneration);
		addEventListener('regenerateAccept', handleSummary);
		addEventListener('regenerateDecline', declineSummaryRegeneration);
		addEventListener('errorRegenerateAccept', handleErrorRegenerate);

		return () => {
			removeEventListener('messageSend', handleMessageSend);
			removeEventListener('generateAccept', handlePotentialSummary);
			removeEventListener('generateDecline', declineSummaryGeneration);
			removeEventListener('regenerateAccept', handleSummary);
			removeEventListener('regenerateDecline', declineSummaryRegeneration);
			removeEventListener('errorRegenerateAccept', handleErrorRegenerate);
		};
	}, [
		messages,
		summaryStories,
		activeStep,
		activeSubStage,
		voiceMode,
		ticketContext,
		hasFinishedSpeaking,
		isSummaryLoading,
		hasAcceptedGenerateRequest,
		choosedFile,
	]);

	const getConversationObject = ({
		messageList,
		withFunctionalRequirements,
	}: {
		messageList: Array<IMessage>;
		withFunctionalRequirements: boolean;
	}): Record<string, string> => {
		return messageList
			.filter(
				(message: IMessage) =>
					message.sender !== MessageSender.System &&
					(message.subStage === activeSubStage ||
						(message.subStage === GeneralStepConversationTopics['Functional requirements'] &&
							withFunctionalRequirements))
			)
			.reduce((accumulator, message, index, source) => {
				if (index % 2 === 0 && source[index + 1] !== undefined) {
					(accumulator as any)[message.content as any] = JSON.stringify({
						response: source[index + 1].content,
						isConversationEnded: false,
					});
				}
				return accumulator;
			}, {});
	};

	useEffect(() => {
		const currentSubStageMessages = messages.filter(
			(message) => message.subStage === activeSubStage
		);

		const subStageHasWelcomeMessage = currentSubStageMessages.length;

		if (
			!subStageHasWelcomeMessage &&
			activeSubStage === GeneralStepConversationTopics['Functional requirements']
		) {
			const welcomeMessage = {
				content: `generalStep.welcomeMessage.${GeneralStepConversationTopics[activeSubStage]}`,
				sender: MessageSender.System,
				sendTime: new Date().toISOString(),
				stage: TicketStages.general,
				subStage: activeSubStage,
				userId: JSON.parse(localStorage.getItem('user') || '{}')?.id || '',
			};

			setMessages([...messages, welcomeMessage]);
		}

		if (
			!subStageHasWelcomeMessage &&
			activeSubStage !== GeneralStepConversationTopics['Functional requirements']
		) {
			const categoriesNames = Object.keys(GeneralStepConversationTopics).filter((key) =>
				isNaN(Number(key))
			);
			setIsChatLoading(true);
			dispatch(setIsTicketUpdate(true));

			processMessageToChatGpt({
				userMessage: defaultWelcomeMessageRequest(categoriesNames[activeSubStage]),
				updatedContext: getUpdatedContext({
					messageList: messages,
					withFunctionalRequirements: true,
				}),
				conversationObject: getConversationObject({
					messageList: messages,
					withFunctionalRequirements: true,
				}),
			})
				.then((welcomeMessage) => setMessages([...messages, welcomeMessage.response]))
				.finally(() => {
					setIsChatLoading(false);
					setTimeout(() => dispatch(triggerTicketSave()), 100);
				});
		}
	}, [activeSubStage, messages, activeTicket?.id]);

	// user stories objects array transformed to string
	const summary: string = useMemo(() => {
		const currentSubStageStories = summaryStories.filter(
			(story) => story.subStage === activeSubStage
		);

		return currentSubStageStories.length
			? [...currentSubStageStories].reduce((accumulator, value, index) => {
				return accumulator + `${index}.${value.data}\n`;
			}, '')
			: '';
	}, [summaryStories, activeSubStage]);

	const reduceUserStoryWithScenarios = (story: ISummaryStory): string =>
		`${commonTranslate('modelsInput.story')}: ${story.data}.` +
		(Array.isArray(story?.summaryScenarios) && story.summaryScenarios.length
			? `${commonTranslate('modelsInput.scenarios')}: ` +
			story.summaryScenarios.reduce(
				(accumulator, scenario, index) => accumulator + `${index + 1}.${scenario.description}`,
				''
			)
			: '');

	// format user and AI messages to send them to chatGPT
	const getUpdatedContext = ({
		messageList,
		withFunctionalRequirements,
	}: {
		messageList: Array<IMessage>;
		withFunctionalRequirements: boolean;
	}) => {
		const filtered = messageList.filter(
			(message: IMessage) => {
				const isNotSystem = message.sender !== MessageSender.System;
				const isNotInvalid = message.action?.type !== MessageActionTypes.invalid;
				const isCorrectSubStage =
					message.subStage === activeSubStage ||
					(message.subStage === GeneralStepConversationTopics['Functional requirements'] &&
						withFunctionalRequirements);

				const passes = isNotSystem && isNotInvalid && isCorrectSubStage;

				return passes;
			}
		);

		const result = filtered.reduce((accumulator: string, message: IMessage) => {
			return (
				accumulator +
				`${message.sender === MessageSender.user
					? generalStepTranslate('commonText.modelInput.user')
					: generalStepTranslate('commonText.modelInput.system')
				} ${message.content} `
			);
		}, '');

		return result;
	};

	const potentialUserStoriesStreamInput = useMemo(() => {
		const functionalRequirements = activeTicket?.ticketSummaries?.filter(
			(story) => story.subStage === GeneralStepConversationTopics['Functional requirements']
		);

		const functionalRequirementsString = functionalRequirements
			? functionalRequirements.reduce(
				(accumulator: string, story: ISummaryStory) => accumulator + story.data + '. ',
				''
			)
			: '';

		return `${generalStepTranslate('commonText.modelInput.description')} ${getUpdatedContext({
			messageList: messages,
			withFunctionalRequirements: true,
		})}. ${generalStepTranslate(
			'commonText.modelInput.existingStories'
		)} ${functionalRequirementsString}`;
	}, [messages, activeSubStage]);

	/*const userStoriesStreamInput = useMemo(() => {
		const currentRequirements = messages
			.filter(
				(message: IMessage) =>
					message.action?.type !== MessageActionTypes.used &&
					message.action?.type !== MessageActionTypes.invalid &&
					message.sender === MessageSender.user &&
					message.subStage === activeSubStage
			)
			.reduce((accumulator: string, message: IMessage) => {
				return (
					accumulator + `${generalStepTranslate('commonText.modelInput.user')} ${message.content}.`
				);
			}, '');

		return `${generalStepTranslate(
			'commonText.modelInput.existingStories'
		)} ${summary}. ${generalStepTranslate(
			'commonText.modelInput.newRequirements'
		)} ${currentRequirements}`;
	}, [messages, summary, activeSubStage]);*/

	const userStoriesStreamInput = useMemo(() => {
		// Récupérer uniquement les nouveaux messages (non utilisés)
		const newMessages = messages.filter(
			(message: IMessage) =>
				message.action?.type !== MessageActionTypes.used &&
				message.action?.type !== MessageActionTypes.invalid &&
				message.sender !== MessageSender.System &&
				message.subStage === activeSubStage
		);

		// Fonction améliorée pour détecter et séparer les requirements multiples
		const splitMultipleRequirements = (content: string): string[] => {
			// Pattern pour détecter "As a [role], I want to..." - pattern typique des user stories
			const userStoryPattern = /As\s+a\s+[^,]+,\s+I\s+want\s+to[^.]*(?:\.[^A]*|$)/gi;
			const matches = content.match(userStoryPattern);

			if (matches && matches.length > 1) {
				// Si on trouve plusieurs patterns "As a...", les séparer
				return matches.map(m => m.trim()).filter(m => m.length > 0);
			}

			// Sinon, essayer les séparateurs classiques
			const separators = [
				/\s+and\s+(?=I\s+want\s+to)/i,  // "and" suivi de "I want to"
				/\.\s+(?=As\s+a\s+)/i,  // Point suivi de "As a"
				/\s+and\s+/i,
				/,\s+(?=\w+)/,  // Virgule suivie d'un mot
			];

			let parts = [content];

			for (const separator of separators) {
				const newParts: string[] = [];
				for (const part of parts) {
					const split = part.split(separator);
					if (split.length > 1) {
						newParts.push(...split.map(s => s.trim()).filter(s => s.length > 0));
					} else {
						newParts.push(part);
					}
				}
				parts = newParts;
			}

			return parts;
		};

		// Séparer les requirements multiples dans chaque message
		const separatedRequirements = newMessages.flatMap((message) => {
			if (message.sender === MessageSender.user) {
				const split = splitMultipleRequirements(message.content);
				return split.map(req => ({
					...message,
					content: req
				}));
			}
			return [message];
		});

		// Utiliser getUpdatedContext avec les messages séparés
		const newRequirementsContext = getUpdatedContext({
			messageList: separatedRequirements,
			withFunctionalRequirements: false,
		});


		if (summary && summary.trim().length > 0) {
			const userRequirements = separatedRequirements
				.filter(m => m.sender === MessageSender.user)
				.map((m, index) => `Requirement ${index + 1}: ${m.content}`)
				.join('\n');

			return `${generalStepTranslate(
				'commonText.modelInput.existingStories'
			)} ${summary}. ${generalStepTranslate(
				'commonText.modelInput.description'
			)} The following are ${separatedRequirements.filter(m => m.sender === MessageSender.user).length} separate requirements. Please generate one user story for each:\n${userRequirements}`;
		} else {
			return `${generalStepTranslate('commonText.modelInput.description')} ${newRequirementsContext}`;
		}
	}, [messages, summary, activeSubStage, generalStepTranslate]);

	const startPotentialUserStoriesStream = async () => {
		const functionalRequirements = activeTicket?.ticketSummaries?.filter(
			(story) => story.subStage === GeneralStepConversationTopics['Functional requirements']
		);

		const functionalRequirementsString = functionalRequirements
			? functionalRequirements.reduce(
				(accumulator: string, story: ISummaryStory) => accumulator + story.data + '. ',
				''
			)
			: '';

		const contextResult = getUpdatedContext({
			messageList: messages,
			withFunctionalRequirements: true,
		});

		let userInput = '';

		if (contextResult && contextResult.trim().length > 0) {
			// Le contexte de conversation (requirements du chat) est la source principale
			userInput = `${generalStepTranslate('commonText.modelInput.description')} ${contextResult}`;

			// Ajouter les functional requirements existantes seulement comme contexte additionnel
			if (functionalRequirementsString && functionalRequirementsString.trim().length > 0) {
				userInput += `. ${generalStepTranslate('commonText.modelInput.existingStories')} ${functionalRequirementsString}`;
			}
		} else {
			// Si pas de contexte de conversation, utiliser les functional requirements
			console.warn('No conversation context found');
			if (functionalRequirementsString && functionalRequirementsString.trim().length > 0) {
				userInput = `${generalStepTranslate('commonText.modelInput.description')} ${generalStepTranslate('commonText.modelInput.existingStories')} ${functionalRequirementsString}`;
			} else {
				// Fallback: utiliser tous les messages disponibles
				userInput = `${generalStepTranslate('commonText.modelInput.description')} ${messages
					.filter((m) => m.sender === MessageSender.user)
					.map((m) => m.content)
					.join('. ')}`;
			}
		}

		return await getPotentialUserStoriesStream({
			userInput: userInput,
			signalName: userStoriesStreamName,
			fileName: choosedFile?.name,
		});
	};

	const startUserStoriesStream = async () => {
		return await getUserStoriesStream({
			userInput: userStoriesStreamInput,
			signalName: userStoriesStreamName,
			fileName: choosedFile?.name,
		});
	};

	const getSendTime = () => {
		const sendTime = new Date();
		sendTime.setTime(new Date().getTime() + 1);
		return sendTime;
	};

	const getGenerateMessage = () => ({
		content: 'generalStep.generateMessage',
		sender: MessageSender.System,
		sendTime: new Date().toISOString(),
		action: {
			type: MessageActionTypes.generate,
			state: MessageActionStates.default,
		},
		stage: activeStep,
		subStage: activeSubStage,
		userId: JSON.parse(localStorage.getItem('user') || '{}')?.id || '',
	});

	const getAfterGenerateMessage = (getFullMessage: boolean) => ({
		content: getFullMessage
			? chatMessagesTranslate('generalStep.postGenerateMessage')
			: 'generalStep.postGenerateMessage',
		sender: MessageSender.System,
		sendTime: getSendTime().toISOString(),
		stage: activeStep,
		subStage: activeSubStage,
		userId: JSON.parse(localStorage.getItem('user') || '{}')?.id || '',
	});

	const getGenerateDeclineMessage = (): IMessage => ({
		content: 'generalStep.generateDeclineMessage',
		sender: MessageSender.System,
		sendTime: getSendTime().toISOString(),
		stage: activeStep,
		subStage: activeSubStage,
		userId: JSON.parse(localStorage.getItem('user') || '{}')?.id || '',
	});

	const getNextRequirementsRequestMessage = () => ({
		content: 'generalStep.regenerateMessage',
		sender: MessageSender.System,
		sendTime: getSendTime().toISOString(),
		action: {
			type: MessageActionTypes.regenerate,
			state: MessageActionStates.default,
		},
		stage: activeStep,
		subStage: activeSubStage,
		userId: JSON.parse(localStorage.getItem('user') || '{}')?.id || '',
	});

	const getShowGenerateMessage = (messageList: Array<IMessage>) => {
		const chatGptQuestions = messageList.filter(
			(message) => message.subStage === activeSubStage && message.sender === MessageSender.chatGPT
		);
		const displayedQuestionsNumber = chatGptQuestions.length - 1;

		return (
			!(displayedQuestionsNumber % questionsBetweenGenerateRequests) &&
			displayedQuestionsNumber !== 0
		);
	};

	const handleMessageSend = async (payload: any) => {
		const currentTicket = Object.assign({}, activeTicket);
		const message: IMessage = { ...payload, stage: TicketStages.general };

		// Compter les caractères sans les retours à la ligne
		const contentWithoutNewLines = message.content.replace(/(\r\n|\n|\r)/gm, '');
		if (contentWithoutNewLines.length <= 4000) {
			dispatch(setIsTyping(true));

			const updatedMessagesList = [...messages, message];
			setMessages(updatedMessagesList);

			const updatedContext = getUpdatedContext({
				messageList: messages,
				withFunctionalRequirements: true,
			});

			try {
				// if user finished main part of conversation with chatGPT, suggest to add custom requirements and regenerate user stories.
				if (hasAcceptedGenerateRequest) {
					const requirementsRequestMessage = getNextRequirementsRequestMessage();

					const messagesWithoutInactiveRegenerate = updatedMessagesList.filter(
						(message: IMessage) =>
							!(
								message?.action?.type === MessageActionTypes.regenerate &&
								message?.action?.state === MessageActionStates.default
							)
					);

					setMessages([...messagesWithoutInactiveRegenerate, requirementsRequestMessage]);

					if (voiceMode) playText(requirementsRequestMessage.content);

					dispatch(setIsTicketUpdate(true));
					dispatch(setIsTyping(false));
				} else {
					// if user didn't finish main part of conversation with chatGPT, continue conversation and send message to chatGPT
					processMessageToChatGpt({
						updatedContext,
						userMessage: message.content,
						conversationObject: getConversationObject({
							messageList: updatedMessagesList,
							withFunctionalRequirements:
								activeSubStage !== GeneralStepConversationTopics['Functional requirements'],
						}),
					})
						.then(async ({ response: res, isConversationEnded, isMessageValid }) => {
							const trimmedResponse: IMessage = {
								...res,
								content: res.content.trim(),
								subStage: activeSubStage,
								userId: JSON.parse(localStorage.getItem('user') || '{}')?.id || '',
								action: {
									type: isMessageValid ? null : MessageActionTypes.invalid,
									state: MessageActionStates.default,
								},
							};

							const validatedMessage = {
								...message,
								action: {
									type: isMessageValid ? null : MessageActionTypes.invalid,
									state: MessageActionStates.default,
								},
							};

							if (currentTicket.id !== activeTicket?.id) {
								setMessages([...messages, trimmedResponse]);
								dispatch(setIsTicketUpdate(true));
							} else {
								const newMessagesList = [...messages, validatedMessage, trimmedResponse];
								// If chatGPT asked questionsBetweenGenerateRequests question or user suggested to finish conversation, show generate message
								if (getShowGenerateMessage(newMessagesList) || isConversationEnded) {
									const notLastMessages = newMessagesList.slice(0, -1);
									const lastMessage = newMessagesList.at(-1);

									// hide chatGPT response
									if (lastMessage) {
										lastMessage.sender = MessageSender.hidden;
										lastMessage.sendTime = getSendTime().toISOString();
										setMessages([...notLastMessages, getGenerateMessage(), lastMessage]);
									}
									// otherwise, display chatGPT response
								} else {
									setMessages(newMessagesList);
									if (voiceMode) playText(newMessagesList?.at(-1)?.content || '');
								}
							}

							dispatch(setIsTicketUpdate(true));
							dispatch(setIsTyping(false));
						})
						.then((res) => {
							dispatch(fetchUserBalance());
							return res;
						})
						.catch((err) => {
							if (err.message === notEnoughTokensErrorMessage) {
								cleanUpChatOnError(updatedMessagesList);
							}
						});
				}
			} catch (error) {
				console.error('Error in handleMessageSend:', error);
				dispatch(setIsTyping(false));
			}
		} else {
			errorNotify(commonTranslate('errors.error'), commonTranslate('errors.messageLengthError'));
		}
	};

	// used message shows that message was already used to generate user stories
	const getUsedMessages = (messageList: Array<IMessage>) => {
		return messageList.map((message: IMessage) =>
			message.stage === TicketStages.general && message.sender === MessageSender.user
				? {
					...message,
					action: {
						...message.action,
						type: MessageActionTypes.used,
						state: MessageActionStates.default,
					},
				}
				: message
		);
	};

	const getDescriptionPromise = (context: string) => {
		setIsDescriptionLoading(true);

		return new Promise((resolve, reject) => {
			getDescription({ stories: context, fileName: choosedFile?.name })
				.then((res) => {
					dispatch(
						updateLocalTicket({
							id: activeTicket?.id,
							description: res.description,
							title: res.title,
						})
					);

					if (!streamMessage) dispatch(setIsSummaryLoading(false));

					resolve(true);
				})
				.catch((err) => {
					console.error('=== ERREUR getDescription ===');
					console.error('err:', err);
					console.error('err.message:', err.message);
					console.error('err.stack:', err.stack);
					if (err.message === notEnoughTokensErrorMessage)
						reject(new Error(notEnoughTokensErrorMessage));
				})
				.finally(() => {
					setIsDescriptionLoading(false);
				});
		});
	};

	const userStoriesStreamResponseParser = (string: string): Array<string> =>
		string
			.split('|')
			.filter((string) => string.trim().length)
			.map((string) => string.replaceAll("'", ''));

	const handleUserStoriesStreamReading = (
		responseStream: ReadableStream
	): Promise<Array<string>> => {
		return new Promise((resolve) => {
			const result: Array<string> = [];
			const reader = responseStream.getReader();

			// Recursive function to read stream chunks
			function readNextChunk() {
				reader.read().then(({ value, done }) => {
					if (done) {
						setIsStreamMessageLoading(false);
						resolve(result);
						return;
					}

					if (value !== undefined) {
						const newTextValue = new TextDecoder().decode(value);

						setStreamMessage((prevState) => [...prevState, newTextValue]);
						result.push(newTextValue);

						dispatch(setIsSummaryLoading(false));

						readNextChunk();
					}
				});
			}

			readNextChunk();
		});
	};

	const uploadUserStoriesOnServer = async (userStoriesObject: Array<ISummaryStory>) => {
		try {
			let updatedUserStories: unknown;

			// Créer de nouvelles références pour chaque user story pour garantir la détection des changements par React
			const userStoriesWithNewReferences = userStoriesObject.map(story => ({ ...story }));

			dispatch(updateLocalTicket({ id: activeTicket?.id, ticketSummaries: userStoriesWithNewReferences }));

			// Ensure transient UI fields are not sent to the backend.
			const formattedUserStories: Array<ISummaryStoryWithId> = userStoriesWithNewReferences.map((story) => {
				const { _lastUpdated, ...restStory } = story as ISummaryStory & { _lastUpdated?: number };
				void _lastUpdated;

				return {
					ticketId: activeTicket?.id || '',
					...restStory,
				};
			});

			if (activeTicket?.ticketSummaries?.length) {
				updatedUserStories = unwrapResult(
					await dispatch(editUserStories({ userStoriesData: formattedUserStories }))
				);
			} else {
				updatedUserStories = unwrapResult(
					await dispatch(saveUserStories({ userStoriesData: formattedUserStories }))
				);
			}

			// Some API responses can return an object instead of an array.
			const freshUpdatedUserStories = Array.isArray(updatedUserStories)
				? updatedUserStories.map((story) => ({ ...story }))
				: userStoriesWithNewReferences.map((story) => ({ ...story }));

			dispatch(
				updateLocalTicket({ id: activeTicket?.id || '', ticketSummaries: freshUpdatedUserStories })
			);

			dispatch(fetchUserBalance());

			activeTicketRef.current = { ...activeTicket, ticketSummaries: freshUpdatedUserStories } as any;
		} catch (e: any) {
			console.error('Error uploading user stories:', e);
			handleNotEnoughTokensError({ message: e?.data });
		}
	};

	const existingUserStories = useMemo(
		() => activeTicket?.ticketSummaries?.map((story) => story.data) || [],

		[activeTicket?.ticketSummaries]
	);


	const streamToUserStoriesParse = (storiesPieces: Array<string>): Array<ISummaryStory> => {
		const combinedText = storiesPieces
			.reduce((acc, string) => acc + string, '')
			.replaceAll("'", '');
	
		let parts = combinedText.split('|').filter((string) => string.trim().length);
	
		if (parts.length === 1) {
			const userStoryPattern = /As\s+a\s+[^,]+,\s+I\s+want\s+to[^.]*(?:\.[^A]*|$)/gi;
			const matches = combinedText.match(userStoryPattern);
			
			if (matches && matches.length > 1) {
				parts = matches.map(m => m.trim()).filter(m => m.length > 0);
			}
		}
	
		return parts
			.map((story: string) => story.trim())
			.filter((story: string) => story.length > 0)
			.map(
				(story: string): ISummaryStory => ({
					data: story.replaceAll('\n', ' ').trim(),
					isDeleted: false,
					subStage: activeSubStage,
				})
			);
	};


	
	const saveNewUserStories = async (newStories: Array<ISummaryStory>) => {
		const userStories = [...summaryStories, ...newStories];

		await uploadUserStoriesOnServer(userStories);
		dispatch(setIsStreaming(false));
	};

	const getUserStoriesPromise = () => {
		return new Promise((resolve, reject) => {
			setIsStreamMessageLoading(true);

			startUserStoriesStream()
				.then(async (response) => {
					const parsedResponse = response.body;

					const resultStories = await handleUserStoriesStreamReading(parsedResponse);

					await saveNewUserStories(streamToUserStoriesParse(resultStories));

					resolve(true);
				})
				.catch((err) => {
					if (err.message === notEnoughTokensErrorMessage) {
						dispatch(setIsStreaming(false));
						reject(new Error(notEnoughTokensErrorMessage));
					}
				});
		});
	};

	const getPotentialUserStoriesPromise = () => {
		return new Promise((resolve, reject) => {
			setIsStreamMessageLoading(true);

			startPotentialUserStoriesStream()
				.then(async (response) => {
					const parsedResponse = response.body;

					const resultStories = await handleUserStoriesStreamReading(parsedResponse);

					await saveNewUserStories(streamToUserStoriesParse(resultStories));

					resolve(true);
				})
				.catch((err) => {
					if (err.message === notEnoughTokensErrorMessage) {
						dispatch(setIsStreaming(false));
						reject(new Error(notEnoughTokensErrorMessage));
					}
				});
		});
	};

	// split messages to hidden and not hidden
	const handleHiddenMessages = (messageList: Array<IMessage>) => {
		const messagesWithoutHidden = messageList.filter(
			(message: IMessage) => message.sender !== MessageSender.hidden
		);
		const hiddenMessages = messageList.filter(
			(message: IMessage) => message.sender === MessageSender.hidden
		);

		return { messagesWithoutHidden, hiddenMessages };
	};

	const handleSummary = async () => {
		try {
			dispatch(setIsSummaryLoading(true));
			dispatch(setIsStreaming(true));

			const updatedContext = getUpdatedContext({
				messageList: messages,
				withFunctionalRequirements: true,
			});

			const dataToFetchArray = [];

			if (!activeTicket?.description) dataToFetchArray.push(getDescriptionPromise(updatedContext));

			dataToFetchArray.push(getUserStoriesPromise());

			Promise.allSettled(dataToFetchArray).then((response) => {
				const hasFailedRequest = response.filter((item) => item.status === 'rejected');
				const hasFailedBecauseOfTokens = response.filter(
					(item) =>
						item.status === 'rejected' && item.reason === `Error: ${notEnoughTokensErrorMessage}`
				);

				if (hasFailedRequest.length) {
					if (hasFailedBecauseOfTokens) handleGenerateReactivate(messages);
				} else {
					const messagesWithoutErrors = messages.filter(
						(message) => message.action?.type !== MessageActionTypes.error
					);

					const updatedMessages = getUsedMessages(
						getMessagesAfterUpdatedSystemState(
							messagesWithoutErrors,
							MessageActionTypes.regenerate,
							MessageActionStates.accept
						)
					);

					dispatch(updateLocalTicket({ id: activeTicket?.id, messages: updatedMessages }));

					setMessages(updatedMessages);
				}

				dispatch(setIsSummaryLoading(false));
				dispatch(setIsStreaming(false));
				dispatch(setIsTicketUpdate(true));
				setTimeout(() => dispatch(triggerTicketSave()), 100);
			});
		} catch (error) {
			console.error('Error in handleSummary:', error);
		}
	};

	const handlePotentialSummary = async () => {
		try {
			dispatch(setIsSummaryLoading(true));
			dispatch(setIsStreaming(true));

			const currentSubStageMessages = messages.filter(
				(message) => message.subStage === activeSubStage
			);

			const currentSubStageUserStories = summaryStories.filter(
				(story) => story.subStage === activeSubStage
			);

			const descriptionContext = getUpdatedContext({
				messageList: messages,
				withFunctionalRequirements: true,
			});

			const dataToFetchArray = [];

			if (!currentSubStageUserStories.length) {
				dataToFetchArray.push(getPotentialUserStoriesPromise());
			}

			Promise.allSettled(dataToFetchArray).then(async (response) => {
				const hasFailedRequest = response.filter((item) => item.status === 'rejected');
				const hasFailedBecauseOfTokens = response.filter(
					(item) =>
						item.status === 'rejected' && item.reason === `Error: ${notEnoughTokensErrorMessage}`
				);

				if (hasFailedRequest.length) {
					if (hasFailedBecauseOfTokens.length) {
						handleGenerateReactivate(messages);
					}
				} else {
					// Générer la description APRÈS avoir les user stories
					const currentTicket = activeTicketRef.current;
					const allUserStories = currentTicket?.ticketSummaries || [];

					const userStoriesString = allUserStories
						.map((story, index) => `${index + 1}. ${story.data}`)
						.join('\n');

					// Générer la description basée sur les user stories
					if ((!activeTicket?.description || !activeTicket?.title) && userStoriesString.length) {
						await getDescriptionPromise(userStoriesString);
					}

					const { messagesWithoutHidden } = handleHiddenMessages(messages);

					const messagesWithoutErrors = messagesWithoutHidden.filter(
						(message) => message.action?.type !== MessageActionTypes.error
					);

					const afterGenerateMessage = getAfterGenerateMessage(false);

					const updatedMessages = getMessagesWithoutHidden(
						getUsedMessages(
							getMessagesAfterUpdatedSystemState(
								[...messagesWithoutErrors, afterGenerateMessage],
								MessageActionTypes.generate,
								MessageActionStates.accept
							)
						)
					);

					dispatch(updateLocalTicket({ id: activeTicket?.id, messages: updatedMessages }));

					setMessages(updatedMessages);

					if (activeTicket?.status === Statuses['Draft']) {
						dispatch(
							updateLocalTicket({
								id: activeTicket?.id,
								status: Statuses['In Progress'],
							})
						);
					}
				}

				dispatch(setIsSummaryLoading(false));
				dispatch(setIsStreaming(false));
				dispatch(setIsTicketUpdate(true));
				setTimeout(() => dispatch(triggerTicketSave()), 100);
			});
		} catch (error) {
			console.error('Error in handlePotentialSummary:', error);
		}
	};

	const generateUserStoriesScenarios = async () => {
		if (!activeTicketRef.current?.ticketSummaries) return;
		try {
			dispatch(setIsSummaryLoading(true));

			const ticketUserStories = activeTicketRef.current.ticketSummaries;

			const userStoriesWithoutScenarios = ticketUserStories.filter(
				(story) => !story?.summaryScenarios?.length
			);

			if (userStoriesWithoutScenarios.length > 0) {
				const userStoriesWithScenariosPromise = ticketUserStories.map(async (story) => {
					if (story?.summaryScenarios?.length) return { ...story };

					const scenario = await getUserStoryScenario({
						userStory: story.data,
						fileName: choosedFile?.name,
					});

					// Créer une nouvelle référence pour l'objet story
					return {
						...story,
						summaryScenarios: scenario,
						// Ajouter un timestamp pour forcer la détection du changement
						_lastUpdated: Date.now()
					};
				});

				const userStoriesWithScenariosPromiseResults = await Promise.allSettled(
					userStoriesWithScenariosPromise
				);

				const userStoriesWithScenarios = userStoriesWithScenariosPromiseResults.reduce(
					(acc, curr) => {
						if (curr?.status === 'fulfilled') {
							return [...acc, curr?.value];
						}
						return acc;
					},
					[] as ISummaryStory[]
				);

				await uploadUserStoriesOnServer(userStoriesWithScenarios);

				if (userStoriesWithScenarios.length < userStoriesWithoutScenarios.length) {
					await generateUserStoriesScenarios();
				} else {
					// Après la génération complète, ouvrir automatiquement la première user story
					// qui a des scénarios dans le substage actuel
					const firstStoryWithScenarios = userStoriesWithScenarios.find(
						(story) => story.summaryScenarios?.length && story.subStage === activeSubStage
					);

					if (firstStoryWithScenarios) {
						dispatch(setDetailedViewTicketSummary(firstStoryWithScenarios));
					}
				}

				dispatch(setIsSummaryLoading(false));
			} else {
				dispatch(setIsSummaryLoading(false));
			}
		} catch (e) {
			console.error('Error during scenarios generation:', e);
			dispatch(setIsSummaryLoading(false));
		}
	};

	const generateUserStoriesAcceptanceCriteria = async () => {
		if (activeTicketRef.current?.ticketSummaries) {
			dispatch(setIsSummaryLoading(true));

			const ticketUserStories = activeTicketRef.current.ticketSummaries;

			const userStoriesWithoutAcceptanceCriteria = ticketUserStories.filter(
				(story) => !story?.summaryAcceptanceCriteria?.length
			);

			if (userStoriesWithoutAcceptanceCriteria.length > 0) {
				const userStoriesWithScenariosPromise = ticketUserStories.map(async (story) => {
					if (story?.summaryAcceptanceCriteria?.length) return { ...story };

					const acceptanceCriteria = await getUserStoryAcceptanceCriteria({
						userStoryWithScenarios: reduceUserStoryWithScenarios(story),
						fileName: choosedFile?.name,
					});

					// Créer une nouvelle référence pour l'objet story
					return {
						...story,
						summaryAcceptanceCriteria: acceptanceCriteria,
						// Ajouter un timestamp pour forcer la détection du changement
						_lastUpdated: Date.now()
					};
				});

				const userStoriesWithAcceptanceCriteriaPromiseResults = await Promise.allSettled(
					userStoriesWithScenariosPromise
				);

				const userStoriesWithAcceptanceCriteria =
					userStoriesWithAcceptanceCriteriaPromiseResults.reduce((acc, curr) => {
						if (curr?.status === 'fulfilled') {
							return [...acc, curr?.value];
						}
						return acc;
					}, [] as ISummaryStory[]);

				await uploadUserStoriesOnServer(userStoriesWithAcceptanceCriteria);

				if (
					userStoriesWithAcceptanceCriteria.length < userStoriesWithoutAcceptanceCriteria.length
				) {
					await generateUserStoriesAcceptanceCriteria();
				} else {
					// Après la génération complète, ouvrir automatiquement la première user story
					// qui a des critères d'acceptation dans le substage actuel
					const firstStoryWithCriteria = userStoriesWithAcceptanceCriteria.find(
						(story) => story.summaryAcceptanceCriteria?.length && story.subStage === activeSubStage
					);

					if (firstStoryWithCriteria) {
						dispatch(setDetailedViewTicketSummary(firstStoryWithCriteria));
					}
				}

				dispatch(setIsSummaryLoading(false));
			} else {
				dispatch(setIsSummaryLoading(false));
			}
		}
	};

	const handleErrorRegenerate = async () => {
		if (activeTicket?.description && summaryStories.length) {
			await handleSummary();
		} else {
			await handlePotentialSummary();
		}
	};

	const handleGenerateReactivate = (messageList: Array<IMessage>) => {
		const acceptedGenerateMessage = getMessagesAfterUpdatedSystemState(
			messageList,
			MessageActionTypes.generate,
			MessageActionStates.accept
		).filter(
			(message) =>
				message.sender !== MessageSender.hidden && message.action?.type !== MessageActionTypes.error
		);

		const errorRegenerateMessage: IMessage = {
			content: 'common.tokensErrorMessage',
			sender: MessageSender.System,
			sendTime: getSendTime().toISOString(),
			stage: activeStep,
			action: {
				type: MessageActionTypes.error,
				state: MessageActionStates.default,
			},
			subStage: activeSubStage,
			userId: JSON.parse(localStorage.getItem('user') || '{}')?.id || '',
		};

		const hiddenMessages = acceptedGenerateMessage.filter(
			(message) => message.sender === MessageSender.hidden
		);

		setMessages([...acceptedGenerateMessage, errorRegenerateMessage, ...hiddenMessages]);
	};

	const processMessageToChatGpt = async ({
		updatedContext,
		userMessage,
		conversationObject,
	}: {
		updatedContext: string;
		userMessage: string;
		conversationObject: Record<string, string>;
	}) => {
		const ChatGPTSubStageTemplateNamesValues = Object.values(ChatGPTSubStageTemplateNames);

		return getConversation({
			conversation: updatedContext,
			userMessage,
			templateName: ChatGPTSubStageTemplateNamesValues[activeSubStage],
			fileName: choosedFile?.name,
			conversationObject: conversationObject,
		})
			.then(({ response, isConversationEnded, isMessageValid }) => {
				const res: IMessage = {
					content: response,
					sender: MessageSender.chatGPT,
					sendTime: new Date().toISOString(),
					stage: activeStep,
					subStage: activeSubStage,
					userId: JSON.parse(localStorage.getItem('user') || '{}')?.id || '',
				};
				return { response: res, isConversationEnded, isMessageValid };
			})
			.catch((err) => {
				if (err.message === notEnoughTokensErrorMessage)
					throw new Error(notEnoughTokensErrorMessage);

				return {
					response: {
						content: '',
						sender: MessageSender.chatGPT,
						sendTime: new Date().toISOString(),
						stage: activeStep,
						userId: JSON.parse(localStorage.getItem('user') || '{}')?.id || '',
					},
					isConversationEnded: false,
					isMessageValid: true,
				};
			});
	};

	const cleanUpChatOnError = (messagesList: Array<IMessage>) => {
		setMessages(messagesList.slice(0, -1));

		dispatch(setIsTyping(false));
	};

	const declineSummaryGeneration = () => {
		const { messagesWithoutHidden } = handleHiddenMessages(
			getMessagesAfterUpdatedSystemState(
				messages,
				MessageActionTypes.generate,
				MessageActionStates.decline
			)
		);

		setMessages([...messagesWithoutHidden, getGenerateDeclineMessage()]);

		dispatch(setIsTicketUpdate(true));
	};

	const declineSummaryRegeneration = () => {
		setMessages(
			getMessagesWithoutHidden(
				getMessagesAfterUpdatedSystemState(
					messages,
					MessageActionTypes.regenerate,
					MessageActionStates.decline
				)
			)
		);

		dispatch(setIsTicketUpdate(true));
	};

	// update system message state according to user interaction with system message
	const getMessagesAfterUpdatedSystemState = (
		messageList: Array<IMessage>,
		type: MessageActionTypes,
		state: MessageActionStates
	) => {
		return messageList.map((message) => {
			const updatedMessage: IMessage = {
				...message,
				action: {
					type: type,
					state: state,
				},
			};

			return message.action?.type === type ? updatedMessage : message;
		});
	};

	// if user declined to generate user stories, reveal hidden chatGPT response
	const getMessagesWithoutHidden = (messageList: Array<IMessage>) => {
		return messageList.map((message) => {
			const updatedMessage = {
				...message,
				sender: MessageSender.chatGPT,
			};

			if (message.sender === MessageSender.hidden) {
				if (voiceMode) playText(updatedMessage?.content || '');
				return updatedMessage;
			} else {
				return message;
			}
		});
	};

	useEffect(() => {
		if (hasFinishedSpeaking) startRecognition();
	}, [hasFinishedSpeaking]);

	useEffect(() => {
		if (activeTicket?.id)
			dispatch(updateLocalTicket({ id: activeTicket.id, ticketSummaries: summaryStories }));
	}, [summaryStories]);

	const getNextButtonText = useMemo(() => {
		const isLastTopic = activeSubStage === getNumericEnumLength(GeneralStepConversationTopics) - 1;

		const areCurrentSubStageStoriesGenerated = activeTicket?.ticketSummaries?.filter(
			(story) => story.subStage === activeSubStage
		)?.length;

		// turn off the useCases text(button) because we are handling this part in other function called  getUseCasesButtonText
		// if (isLastTopic) {
		// 	if (activeTicket?.useCases?.length) return 'goToUseCases';
		// 	return 'generateUseCases';
		// }


		if (!areCurrentSubStageStoriesGenerated) {
			return 'skipCategory';
		}

		return 'goTo' + GeneralStepConversationTopics[activeSubStage + 1];
	}, [activeTicket?.ticketSummaries, activeTicket?.useCases, activeSubStage]);

	const isLastTopic = useMemo(
		() => activeSubStage === getNumericEnumLength(GeneralStepConversationTopics) - 1,
		[activeSubStage]
	);

	const handleNextStepNavigation = () => {
		// const isLastTopic = activeSubStage === getNumericEnumLength(GeneralStepConversationTopics) - 1;
		// const isUseCasesStepNotVisited = TicketStages.useCases > (activeTicket?.currentStep || 0);

		// if (isUseCasesStepNotVisited) dispatch(triggerTicketSave());

		// if (isLastTopic) {
		// 	dispatch(setActiveStep(TicketStages.useCases));
		// } else {
		// 	dispatchEvent('subStageChange', GeneralStepConversationTopics[activeSubStage + 1]);
		// 	dispatch(setActiveSubStage(activeSubStage + 1));
		// }

		const isLastTopic = activeSubStage === getNumericEnumLength(GeneralStepConversationTopics) - 1;
		const isUseCasesStepNotVisited = TicketStages.useCases > (activeTicket?.currentStep || 0);

		// if (isUseCasesStepNotVisited) dispatch(triggerTicketSave());

		// if (isLastTopic) {
		// 	dispatch(setActiveStep(TicketStages.useCases));
		// } else {
		// 	// dispatchEvent('subStageChange', GeneralStepConversationTopics[activeSubStage + 1]);
		// 	// dispatch(setActiveSubStage(activeSubStage + 1));

		// }
		dispatchEvent('subStageChange', GeneralStepConversationTopics[activeSubStage + 1]);
		dispatch(setActiveSubStage(activeSubStage + 1));
	};

	const isNextStageButtonDisplayed = useMemo(
		() =>
			!isSummaryLoading &&
			activeTicket?.description &&
			summaryStories.length &&
			isNavigationButtonAvailable,
		[
			isSummaryLoading,
			activeTicket?.description,
			activeSubStage,
			summaryStories,
			isNavigationButtonAvailable,
		]
	);

	// if it is last stage. User has to generate scenarios and criteria for all existing stories to move to use cases.
	const isNextStageButtonDisabled = useMemo(() => {
		const isLastTopic = activeSubStage === getNumericEnumLength(GeneralStepConversationTopics) - 1;
		const areScenariosAndCriteriaGenerated = summaryStories.every(
			(summary) => summary.summaryAcceptanceCriteria?.length && summary.summaryScenarios?.length
		);

		return isLastTopic && !areScenariosAndCriteriaGenerated;
	}, [activeSubStage, summaryStories]);



	//////////////////// new functions for the new button //////////////////////
	// Add a new function to handle use cases navigation
	const handleUseCasesNavigation = () => {
		const isUseCasesStepNotVisited = TicketStages.useCases > (activeTicket?.currentStep || 0);

		if (isUseCasesStepNotVisited) {
			dispatch(triggerTicketSave());
		}


		// Navigate to use cases step
		dispatch(setActiveStep(TicketStages.useCases));
	};

	// Add a new useMemo for the button text
	const getUseCasesButtonText = useMemo(() => {
		if (activeTicket?.useCases?.length) {
			return 'goToUseCases';
		}
		return 'generateUseCases';
	}, [activeTicket?.useCases]);

	// Add a useMemo to determine if the use cases button should be disabled
	const isUseCasesButtonDisabled = useMemo(() => {
		// Check if current substage has at least one story with scenarios
		const hasCurrentSubstageStories = summaryStories.some(
			(summary) => summary.summaryScenarios?.length
		);

		return !hasCurrentSubstageStories || isChatLoading;
	}, [summaryStories, isChatLoading]);


	//////////////////////////////////// ends here //////////////////////////////

	return (
		<>
			{activeTicket && generalStepTranslate ? (
				<Box
					className="rtl-able"
					data-testid={'general-step'}
					sx={{
						display: 'flex',
						width: '100%',
						height: '100%',
						position: 'relative',
					}}
				>
					{editMode && activeTicket?.description ? (
						<EditGeneralStep ticketTitle={ticketTitle} setTitleError={setTitleError} />
					) : (
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								justifyContent: 'flex-start',
								width: '100%',
							}}
						>
							<Typography
								sx={{
									fontSize: '16px',
									fontWeight: 700,
									marginBottom: '24px',
									color: titleTextColor,
								}}
							>
								{generalStepTranslate(`substages.${GeneralStepConversationTopics[activeSubStage]}`)}
							</Typography>
							{isSummaryLoading ? (
								<Box
									sx={{
										height: '100%',
										width: '100%',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
									}}
								>
									<Oval
										height={24}
										width={24}
										color="#fff"
										wrapperStyle={{}}
										wrapperClass=""
										visible={true}
										ariaLabel="oval-loading"
										secondaryColor="#4786ff"
										strokeWidth={5}
										strokeWidthSecondary={5}
									/>
								</Box>
							) : activeTicket?.description ||
								activeTicket?.ticketSummaries?.length ||
								streamMessage.length ? (
								<Box
									sx={{
										display: 'flex',
										flexDirection: 'column',
										height: 'calc(100% - 80px)',
										overflow: 'auto',
										paddingLeft: i18n?.language === 'ar' ? '10px' : '0',
									}}
								>
									<TitleText
										text={generalStepTranslate('commonText.description')}
										styles={{ margin: '0 10px 5px 0', fontWeight: '400' }}
									/>

									{isDescriptionLoading ? (
										<Box
											sx={{
												width: '100%',
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												padding: '16px 0',
											}}
										>
											<Oval
												height={24}
												width={24}
												color="#fff"
												wrapperStyle={{}}
												wrapperClass=""
												visible={true}
												ariaLabel="oval-loading"
												secondaryColor="#4786ff"
												strokeWidth={5}
												strokeWidthSecondary={5}
											/>
										</Box>
									) : (
										<Typography
											sx={{
												wordWrap: 'break-word',
												whiteSpace: 'pre-line',
												paddingRight: '10px',
												marginBottom: '12px',
												color: regularTextColor,
											}}
										>
											{activeTicket?.description}
										</Typography>
									)}
									{isStreamMessageLoading ? (
										<>
											<Typography
												sx={{
													margin: '8px 10px 5px 0',
													color: titleTextColor,
												}}
											>
												{commonTranslate('modelsInput.userStories')}:
											</Typography>

											<UserStoriesStreaming
												textPieces={streamMessage}
												responseParser={userStoriesStreamResponseParser}
												existingMessage={existingUserStories}
											/>
										</>
									) : (
										<>
											<TitleText
												text={`${commonTranslate('modelsInput.userStories')}:`}
												styles={{
													margin: '8px 10px 5px 0',
													fontWeight: '400',
												}}
											/>

											<GeneralStepSummary ticketSummaries={summaryStories} />
										</>
									)}
								</Box>
							) : (
								<Box
									sx={{
										overflow: 'auto',
										display: 'flex',
										flexDirection: 'column',
									}}
								>
									<Typography sx={{ fontWeight: 700, marginBottom: '5px' }}>
										{generalStepTranslate('commonText.introduction')}
									</Typography>

									<Typography
										sx={{
											wordWrap: 'break-word',
											whiteSpace: 'pre-line',
											paddingRight: '10px',
										}}
									>
										{generalStepTranslate('guide.introduction')}
									</Typography>

									<Typography sx={{ fontWeight: 700, margin: '16px 0 5px' }}>
										{generalStepTranslate('commonText.howTo')}
									</Typography>
									<Box
										data-testid={'how-to-block'}
										sx={{
											display: 'flex',
											flexDirection: 'column',
											gap: '16px',
										}}
									>
										{[
											generalStepTranslate('guide.howTo.step1'),
											generalStepTranslate('guide.howTo.step2'),
											generalStepTranslate('guide.howTo.step3'),
											generalStepTranslate('guide.howTo.step4'),
											generalStepTranslate('guide.howTo.step5'),
											generalStepTranslate('guide.howTo.step6'),
											generalStepTranslate('guide.howTo.step7'),
											generalStepTranslate('guide.howTo.step8'),
											generalStepTranslate('guide.howTo.step9'),
											generalStepTranslate('guide.howTo.step10'),
										].map((line, index) => (
											<Typography
												key={index}
												sx={{
													wordWrap: 'break-word',
													whiteSpace: 'pre-line',
													paddingRight: '10px',
												}}
												dangerouslySetInnerHTML={{ __html: line }}
											/>
										))}
									</Box>
								</Box>
							)}

							{(areScenariosGenerated ||
								areAcceptanceCriteriaGenerated ||
								isNextStageButtonDisplayed) &&
								!isSummaryLoading ? (
								<>
									<Divider sx={{ margin: '16px 0', background: '#E7E8EC' }} />

									<Box
										sx={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}
										data-testid={'user-stories-block'}
									>
										<Box sx={{ display: 'flex', gap: '8px' }}>
											{areScenariosGenerated ? (
												<Styled.StyledButton
													className="not-rtl-able"
													variant="contained"
													color="primary"
													onClick={generateUserStoriesScenarios}
													sx={{ maxWidth: '180px' }} // max width of the button
												>
													<Typography
														sx={{
															whiteSpace: 'nowrap',
															overflow: 'hidden',
															textOverflow: 'ellipsis',
														}}
													>
														{generalStepTranslate('buttons.generateScenarios')}
													</Typography>
													<Stars style={{ marginLeft: '4px', width: '16px' }} />
												</Styled.StyledButton>
											) : null}

											{areAcceptanceCriteriaGenerated ? (
												<Styled.StyledButton
													className="not-rtl-able"
													disabled={areScenariosGenerated}
													variant="contained"
													color="primary"
													onClick={generateUserStoriesAcceptanceCriteria}
													sx={{ maxWidth: '180px' }} // Limit width to avoid stretching
												>
													<Typography
														sx={{
															whiteSpace: 'nowrap',
															overflow: 'hidden',
															textOverflow: 'ellipsis',
														}}
													>
														{generalStepTranslate('buttons.generateAcceptanceCriteria')}
													</Typography>
													<Stars style={{ marginLeft: '4px', width: '16px' }} />
												</Styled.StyledButton>
											) : null}
										</Box>
										<Box sx={{ display: 'flex', gap: '8px' }}>
											{/* New Use Cases Button - Always visible at any substage */}
											<CustomActionTooltip
												tooltipMessage={commonTranslate('tooltips.goToUseCasesButtonDisabled')}
												check={isUseCasesButtonDisabled}
												styles={{ maxWidth: 400 }}
											>
												<Styled.StyledButton
													className="not-rtl-able"
													variant="contained"
													color="primary"
													disabled={isUseCasesButtonDisabled}
													onClick={handleUseCasesNavigation}
												// sx={{ maxWidth: '160px' }} // Limit width to avoid stretching
												>
													<Typography
														sx={{
															whiteSpace: 'nowrap',
															overflow: 'hidden',
															textOverflow: 'ellipsis',
														}}
													>
														{generalStepTranslate(`buttons.${getUseCasesButtonText}`)}
													</Typography>
													<Stars style={{ marginLeft: '4px', width: '16px' }} />
												</Styled.StyledButton>
											</CustomActionTooltip>

											{/* Original Next Category Button */}
											{isNextStageButtonDisplayed && !isLastTopic ? (
												<CustomActionTooltip
													tooltipMessage={commonTranslate('tooltips.goToUseCasesButtonDisabled')}
													check={isNextStageButtonDisabled}
													styles={{ maxWidth: 400 }}
												>

													<Styled.StyledButton
														className="not-rtl-able"
														// variant={isLastTopic ? 'contained' : 'outlined'}
														variant='outlined'
														color="primary"
														disabled={isChatLoading || isNextStageButtonDisabled}
														onClick={handleNextStepNavigation}
													// sx={{ maxWidth: '160px' }} // Limit width to avoid stretching
													>
														<Typography
															sx={{
																whiteSpace: 'nowrap',
																overflow: 'hidden',
																textOverflow: 'ellipsis',
															}}
														>
															{generalStepTranslate(`buttons.${getNextButtonText}`)}
														</Typography>
														{/* {isLastTopic ? (
														<Stars style={{ marginLeft: '4px', width: '20px' }} />
													) : (
														<ArrowLeft style={{ transform: 'rotate(180deg)' }} />
													)} */}

														<ArrowLeft style={{ transform: 'rotate(180deg)' }} />

													</Styled.StyledButton>
												</CustomActionTooltip>
											) : null}
										</Box>

									</Box>
								</>
							) : null}
						</Box>
					)}
				</Box>
			) : null}
		</>
	);
};